import React, { useEffect, useState } from "react";
import api from "../../utils/axiosInstance";
import AdminSideMenu from "../../components/AdminSideMenu";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AiFillDelete } from "react-icons/ai";

const CarrierDemoBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage] = useState(15);
    const [filterDate, setFilterDate] = useState(null);
    const [timeFilter, setTimeFilter] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [loading, setLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        fetchBookings();
    }, [currentPage, filterDate, timeFilter]);

    const fetchBookings = async (page = currentPage) => {
        try {
            setLoading(true);
            const params = {
                page: page,
                limit: itemsPerPage
            };

            if (filterDate) {
                const year = filterDate.getFullYear();
                const month = String(filterDate.getMonth() + 1).padStart(2, '0');
                const day = String(filterDate.getDate()).padStart(2, '0');
                params.date = `${year}-${month}-${day}`;
            }

            if (timeFilter) {
                params.filter = timeFilter;
            }

            console.log('Sending request with params:', params);

            const res = await api.get("/demo-slots/booked", { params });
            const data = res.data;

            setBookings(data.bookedSlots || []);
            setTotalPages(data.totalPages || 1);
            setTotalItems(data.total || 0);
            setCurrentPage(data.currentPage || 1);

        } catch (err) {
            console.error("Failed to fetch bookings:", err);
        } finally {
            setLoading(false);
        }
    };

    // Handle time filter change
    const handleTimeFilterChange = (filter) => {
        setTimeFilter(filter);
        setCurrentPage(1);
        setFilterDate(null);
    };

    // Handle date filter change
    const handleDateChange = (date) => {
        setFilterDate(date);
        setCurrentPage(1);
        setTimeFilter("");
    };

    const clearAllFilters = () => {
        setFilterDate(null);
        setTimeFilter("");
        setCurrentPage(1);
        setTimeout(() => fetchBookings(1), 0);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleDeleteClick = (booking) => {
        setSelectedBooking(booking);
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/demo-slots/delete/${selectedBooking._id}`);
            setShowModal(false);
            setSelectedBooking(null);
            fetchBookings(1); // Reset to first page after delete
        } catch (err) {
            console.error("Error deleting booking:", err);
            setShowModal(false);
            setSelectedBooking(null);
        }
    };

    // Format date for display
    const formatDisplayDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // Check if any filter is active
    const isFilterActive = filterDate || timeFilter;

    return (
        <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
            <AdminSideMenu />

            <div className="w-full md:ml-64 p-4 sm:p-6 lg:p-8">
                {/* Header */}
                <header className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Live Demo Bookings</h1>
                        </div>
                        <div>
                            <a href="https://demo.fleetycdl.com/" target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-800 transition">
                                Go to Demo App
                            </a>
                        </div>
                    </div>

                    <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <label className="text-gray-700 font-medium whitespace-nowrap">Search by Date:</label>
                                <div className="flex items-center gap-2">
                                    <DatePicker
                                        selected={filterDate}
                                        onChange={handleDateChange}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        className="px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-600 w-full sm:w-auto"
                                    />
                                    {filterDate && (
                                        <button
                                            onClick={() => {
                                                setFilterDate(null);
                                                setCurrentPage(1);
                                            }}
                                            className="px-3 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition text-sm"
                                            title="Clear date filter"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <label className="text-gray-700 font-medium whitespace-nowrap">Filter by Time:</label>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={timeFilter}
                                        onChange={(e) => handleTimeFilterChange(e.target.value)}
                                        className="px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-600 w-full sm:w-auto"
                                    >
                                        <option value="">All Bookings</option>
                                        <option value="today">Today</option>
                                        <option value="previous">Previous</option>
                                        <option value="upcoming">Upcoming</option>
                                    </select>
                                    {timeFilter && (
                                        <button
                                            onClick={() => {
                                                setTimeFilter("");
                                                setCurrentPage(1);
                                            }}
                                            className="px-3 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition text-sm"
                                            title="Clear time filter"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>

                            {(filterDate || timeFilter) && (
                                <button
                                    onClick={clearAllFilters}
                                    className="px-4 py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600 transition whitespace-nowrap"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                    </div>
                </header>

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-2 text-gray-600">Loading bookings...</p>
                    </div>
                )}

                {/* Booking Cards Grid */}
                {!loading && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 items-stretch">
                            {bookings.length === 0 ? (
                                <div className="col-span-full text-center text-gray-500 text-lg py-12">
                                    No bookings found
                                </div>
                            ) : (
                                bookings.map((booking) => (
                                    booking.email != null &&
                                    <div
                                        key={booking._id}
                                        className="relative h-full flex flex-col bg-white shadow-lg hover:shadow-xl hover:-translate-y-1 hover:scale-[1.01] transition rounded-3xl overflow-hidden"
                                    >
                                        {/* Header */}
                                        <div className="bg-blue-700 py-6 text-center text-white">
                                            <div className="text-lg font-bold">
                                                Slot: {booking.startTime} - {booking.endTime}
                                            </div>
                                            <div className="text-sm font-medium">
                                                Date: {formatDisplayDate(booking.date)}
                                            </div>
                                            <button
                                                onClick={() => handleDeleteClick(booking)}
                                                className="absolute top-2 right-2 text-white hover:text-red-400 transition"
                                                title="Delete Booking"
                                            >
                                                <AiFillDelete size={22} />
                                            </button>
                                        </div>

                                        {/* Body */}
                                        <div className="flex-1 flex flex-col px-6 py-6">
                                            <ul className="mb-6 space-y-3 text-gray-700 text-sm">
                                                <li>
                                                    <span className="font-semibold">Name:</span> {booking.name}
                                                </li>
                                                <li>
                                                    <span className="font-semibold">Phone:</span> {booking.phone}
                                                </li>
                                                <li>
                                                    <span className="font-semibold">Email:</span> {booking.email}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center mt-8 space-x-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 disabled:opacity-50 hover:bg-gray-300 transition"
                                >
                                    Prev
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i + 1}
                                        onClick={() => handlePageChange(i + 1)}
                                        className={`px-4 py-2 rounded-lg transition ${currentPage === i + 1
                                            ? "bg-blue-600 text-white"
                                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            }`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 disabled:opacity-50 hover:bg-gray-300 transition"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Delete Confirmation Modal */}
                {showModal && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white rounded-xl shadow-lg p-6 w-11/12 max-w-md">
                            <h2 className="text-lg font-semibold mb-4">
                                Remove Demo Booking?
                            </h2>
                            <p className="text-gray-700 mb-6">
                                Are you sure you want to remove the demo booking for{" "}
                                <span className="font-semibold">{selectedBooking?.name}</span>?
                            </p>
                            <div className="flex justify-end gap-4">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400 transition"
                                >
                                    No
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition"
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CarrierDemoBookings;